import { Timestamp } from 'firebase/firestore';
import { DEFAULT_BRANCH_SEED } from './branchMatching';

// ============================================================================
// SHARED TYPES
// Types used across multiple modules (CRM, Support, Fleet, etc.)
// ============================================================================

/**
 * Structured address for consistent formatting across the app
 * Used in: Leads, Support Tickets, Equipment, Customers
 */
export interface Address {
  street1: string;
  street2?: string;
  city: string;
  state: string;
  zip: string;
  country?: string;
  /** Original unparsed string (from AppSheet or single-line input) */
  raw?: string;
  /** Whether address has been validated/parsed */
  isValidated?: boolean;
  /** Geocoded coordinates (from Google Maps API) */
  lat?: number;
  lng?: number;
}

/**
 * Format an Address object as a single-line string
 */
export function formatAddress(address: Address | undefined | null): string {
  if (!address) return '';
  const parts = [
    address.street1,
    address.street2,
    address.city,
    address.state,
    address.zip,
  ].filter(Boolean);
  return parts.join(', ');
}

/**
 * Format address for Google Maps URL
 */
export function formatAddressForMaps(address: Address | undefined | null): string {
  if (!address) return '';
  return encodeURIComponent(formatAddress(address));
}

/**
 * Get Google Maps search URL for an address
 */
export function getGoogleMapsUrl(address: Address | undefined | null): string {
  if (!address) return '';
  return `https://www.google.com/maps/search/?api=1&query=${formatAddressForMaps(address)}`;
}

/**
 * Get Google Maps embed URL for an address (for iframe)
 */
export function getGoogleMapsEmbedUrl(address: Address | undefined | null, apiKey: string): string {
  if (!address) return '';
  return `https://www.google.com/maps/embed/v1/place?key=${apiKey}&q=${formatAddressForMaps(address)}`;
}

// ============================================================================
// STATUS NOTES
// ============================================================================

/**
 * Individual status note with metadata
 * Replaces simple text fields to track who wrote what and when
 * Used in: Support Tickets, Call Logs (future), Service Tickets (future)
 */
export interface StatusNote {
  noteId: string;
  content: string;
  createdBy: string;        // User ID
  createdByName: string;    // Display name (denormalized for display)
  createdAt: Timestamp;
  /** Source system that created this note */
  source: 'appsheet' | 'officehub';
  /** Optional: was this note edited? */
  editedAt?: Timestamp;
  editedBy?: string;
}

/**
 * Sort status notes by date (newest first by default)
 */
export function sortStatusNotes(notes: StatusNote[], order: 'asc' | 'desc' = 'desc'): StatusNote[] {
  return [...notes].sort((a, b) => {
    const aTime = a.createdAt?.toMillis() || 0;
    const bTime = b.createdAt?.toMillis() || 0;
    return order === 'desc' ? bTime - aTime : aTime - bTime;
  });
}

// ============================================================================
// BRANCH CONFIGURATION
// ============================================================================

/**
 * Branch entity - physical office/service location
 * Stored in organization settings for centralized management
 */
export interface Branch {
  branchId: string;
  /** Display name: "Seaside", "Tracy", "Reno" */
  name: string;
  /** Lowercase key for lookups: "seaside", "tracy", "reno" */
  code: string;
  /** Can be deactivated without deletion */
  isActive: boolean;
  /** For consistent ordering in dropdowns */
  sortOrder: number;
  /** Branch physical address */
  address?: Address;
  /** Branch phone number */
  phone?: string;
  /** Branch email */
  email?: string;
  /** Timezone: e.g., "America/Los_Angeles" */
  timezone?: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

/**
 * Branch configuration document
 * Collection: /organizations/{orgId}/settings/branches
 */
export interface BranchConfig {
  branches: Branch[];
  defaultBranchId?: string;
}

/**
 * Legacy Branch type (for backwards compatibility during migration)
 * TODO: Remove after full migration to BranchConfig
 */
export type LegacyBranch = 'Seaside' | 'Tracy' | 'Reno';

/**
 * Map legacy branch string to branch code
 */
export function legacyBranchToCode(branch: LegacyBranch | string): string {
  return branch.toLowerCase().trim();
}

/** A usable, active branch: exactly `isActive === true` and a non-blank branchId. */
function isOfferable(b: Partial<Branch> | null | undefined): boolean {
  return !!b && b.isActive === true && typeof b.branchId === 'string' && b.branchId.trim() !== '';
}

function bySortOrder(a: Pick<Branch, 'sortOrder'>, b: Pick<Branch, 'sortOrder'>): number {
  return (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
}

/**
 * Get active branches sorted by sortOrder.
 *
 * Active means exactly `isActive === true` (slice E review F7; this was truthy, so a stored
 * `"yes"` or `1` counted as active here while the server's case-branch check refused it).
 */
export function getActiveBranches(config: BranchConfig | undefined): Branch[] {
  if (!Array.isArray(config?.branches)) return [];
  return config!.branches.filter(isOfferable).sort(bySortOrder);
}

/**
 * The branches to offer, under the SAME empty-config rule as the server
 * (functions/src/support-module/services/caseBranch.ts, "THE EMPTY-CONFIG RULE"):
 *   - no config doc, or a doc without a `branches` array -> DEFAULT_BRANCHES;
 *   - an existing array -> only its usable active entries, which may be none (no fallback).
 */
export function branchesOffered(config: BranchConfig | null | undefined): Branch[] {
  if (!config || !Array.isArray(config.branches)) {
    return (DEFAULT_BRANCHES.filter(isOfferable) as Branch[]).sort(bySortOrder);
  }
  return getActiveBranches(config);
}

export { branchesForMatching } from './branchMatching';
export type { MatchableBranch } from './branchMatching';

/**
 * Find branch by ID
 */
export function getBranchById(config: BranchConfig | undefined, branchId: string): Branch | undefined {
  return config?.branches.find(b => b.branchId === branchId);
}

/**
 * Find branch by code (case-insensitive)
 */
export function getBranchByCode(config: BranchConfig | undefined, code: string): Branch | undefined {
  const normalizedCode = code.toLowerCase().trim();
  return config?.branches.find(b => b.code === normalizedCode);
}

/**
 * Find branch by legacy name (for migration)
 */
export function getBranchByLegacyName(config: BranchConfig | undefined, name: string): Branch | undefined {
  const normalizedName = name.trim();
  return config?.branches.find(b =>
    b.name === normalizedName ||
    b.code === normalizedName.toLowerCase()
  );
}

// ============================================================================
// BRANCH DISPLAY CONFIG
// ============================================================================

/**
 * Display configuration for branches
 */
export const BRANCH_DISPLAY_CONFIG: Record<string, { color: string; bgColor: string }> = {
  seaside: { color: 'text-blue-700', bgColor: 'bg-blue-100' },
  tracy: { color: 'text-green-700', bgColor: 'bg-green-100' },
  reno: { color: 'text-purple-700', bgColor: 'bg-purple-100' },
  // Default for unknown branches
  default: { color: 'text-slate-700', bgColor: 'bg-slate-100' },
};

/**
 * Get display config for a branch
 */
export function getBranchDisplayConfig(branchCode: string | undefined): { color: string; bgColor: string } {
  if (!branchCode) return BRANCH_DISPLAY_CONFIG.default;
  return BRANCH_DISPLAY_CONFIG[branchCode.toLowerCase()] || BRANCH_DISPLAY_CONFIG.default;
}

// ============================================================================
// INITIAL BRANCH DATA
// ============================================================================

/**
 * Default branch data for seeding
 * Used when setting up a new organization or migrating existing data
 */
export const DEFAULT_BRANCHES: Omit<Branch, 'createdAt' | 'updatedAt'>[] = DEFAULT_BRANCH_SEED.map((b) => ({ ...b }));
